<template>
  <form @submit="onSubmit">
    <BaseCard
      class="relative mx-auto w-full max-w-sm overflow-hidden"
    >
      <div class="space-y-6">
        <FormInputText name="email" label="Email" />
        <FormInputText
          name="firstName"
          label="First Name"
        />
        <FormInputText name="lastName" label="Last Name" />

        <FormSubmitButton
          :is-submitting="isSubmitting"
          class="w-full"
        >
          {{ userId ? "Update" : "Create" }} User
        </FormSubmitButton>
      </div>

      <BaseInnerLoading v-if="userId && isRetrievingUser" />
    </BaseCard>
  </form>

  <ErrorModal
    :is-open="isErrorModalOpen"
    message="Could not retrieve user"
    description="Something went wrong fetching user details"
    :go-back-to="{
      name: 'usersIndex',
      params: {},
    }"
    go-back-label="Return to user list"
  />
</template>

<script setup lang="ts">
import {
  type CreateUpdateUserFormOutput,
  useCreateUpdateUserForm,
} from "@/composables/forms/useCreateUpdateUserForm"
import { ApiError } from "@/api"

const emit = defineEmits<{
  (
    event: "submit",
    values: CreateUpdateUserFormOutput
  ): void
}>()

const props = defineProps<{
  userId?: string
}>()

const router = useRouter()

const { handleSubmit, setErrors, setValues, isSubmitting } =
  useCreateUpdateUserForm()

const isRetrievingUser = ref(false)
const isErrorModalOpen = ref(false)

onMounted(async () => {
  if (props.userId !== undefined) {
    try {
      isRetrievingUser.value = true
      const user = await retrieveUser(props.userId)
      setValues({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      })
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isRetrievingUser.value = false
    }
  }
})

const onSubmit = handleSubmit(async (formValues) => {
  try {
    await (props.userId
      ? updateUser(props.userId, { ...formValues })
      : createUser(formValues))
    toast.success(
      props.userId ? "User updated" : "User created"
    )
    router.push({ name: "usersIndex" })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.userId
        ? "Failed to update user"
        : "Failed to create user"
    )
    throw error
  }
})
</script>
